import { Injectable } from '@angular/core';
import { MergeTagStore } from './merge-tag.store';
import { MergeTagDto } from '../../../../../../api/src/creative-unit-package/models/merge-tag/merge-tag.dto';
import { CreativeUnitPackageQuery } from '../ad/ad-unit-package.query';
import { debounceTime, Observable, Subscription } from 'rxjs';
import { MergePropertiesUtils } from '../../../../../../api/src/creative-unit-package/utils/merge-properties.utils';
import { CreativeUnitPackageDto } from '../../../../../../api/src/creative-unit-package/models/creative-unit-package.dto';
import { mergeTagEvaluationErrors$, MergeTagEvaluationError } from '../../../../../../api/src/_core/utils/utils.merge-tags';
import { throttleTime } from 'rxjs/operators';
import { GlobalService } from '../global';

@Injectable({ providedIn: 'root' })
export class MergeTagService {
	private activePackageSubscription: Subscription;

	public evaluationErrorSubscription: Subscription;
	private readonly evaluationErrors$: Observable<MergeTagEvaluationError> = mergeTagEvaluationErrors$.asObservable();

	constructor(
		private readonly mergeTagStore: MergeTagStore,
		private readonly creativeUnitPackageQuery: CreativeUnitPackageQuery,
		private readonly globalService: GlobalService
	) {}

	public listenToActivePackageChanges() {
		this.activePackageSubscription = this.creativeUnitPackageQuery
			.selectActive()
			.pipe(debounceTime(1500))
			.subscribe(activePackage => {
				// console.log('Updating merge tags', activePackage);
				const tags = this.processPackageForMergeTags(activePackage);
				this.mergeTagStore.set(tags);
			});
	}

	private processPackageForMergeTags(pkg: CreativeUnitPackageDto) {
		let newMergeTagSuggestions: string[] = [];
		const getMergeableProperties = MergePropertiesUtils.getMergeableProperties(pkg);

		// console.log('getMergeableProperties', getMergeableProperties);

		// process all properties of the package for merge tags
		for (const key in getMergeableProperties) {
			if (key === 'variables') {
				this.extractJsonKeysFromObject(getMergeableProperties[key], 'variables').forEach(variableTag => {
					newMergeTagSuggestions.push(`{{${variableTag}}}`);
				});
			} else {
				// console.log(key);
				newMergeTagSuggestions.push(...this.extractMergeTags(getMergeableProperties[key]));
			}
		}

		// filter out duplicates
		newMergeTagSuggestions = [...new Set(newMergeTagSuggestions)];
		// console.log('newMergeTagSuggestions', newMergeTagSuggestions);

		// format the suggestions for the store
		return newMergeTagSuggestions.map(
			(tag, id) =>
				({
					id: id.toString(),
					label: null,
					value: tag
				} as MergeTagDto)
		);
	}

	/**
	 * Listens to the merge tag evaluation errors observable to trigger a global error message when an error occurs.
	 */
	public listenToEvaluationErrors() {
		this.evaluationErrorSubscription = this.evaluationErrors$.pipe(
			throttleTime(150)
		).subscribe((err: MergeTagEvaluationError) => {
			this.globalService.triggerErrorMessage(
				undefined,
				$localize `:Error Message@@errorMessageMergeTagEvaluation:Error evaluating merge tag expression:\n\n${err.expression}\n\n${err.errorMessage}`,
				{
					duration: 20000,
					panelClass: ['danger', 'multi-line', 'max-width-70']
				}
			);
		});
	}

	/**
	 * Extracts merge tags from an object by stringifying it and matching all occurrences of {{tag}}.
	 * Add universal formatting to the tags.
	 */
	private extractMergeTags(obj: any): string[] {
		if(!obj) {
			return [];
		}
		const objStr = JSON.stringify(obj);

		return Array.from(objStr.matchAll(/\{\{\s*(.*?)\s*}}/g)).map(match => '{{' + match[1] + '}}');
	}

	/**
	 * Extracts all keys from a JSON object, while respecting nesting.
	 * The keys are formatted as dot-separated paths.
	 */
	private extractJsonKeysFromObject(obj: any, parentKey: string = ''): string[] {
		const keys: string[] = [];

		for (const [key, value] of Object.entries(obj)) {
			const fullKey = `${parentKey}.${key}`;

			if (typeof value === 'object' && value !== null) {
				keys.push(...this.extractJsonKeysFromObject(value, fullKey));
			} else {
				keys.push(fullKey);
			}
		}

		return keys;
	}

	/**
	 * Unsubscribes from the active subscriptions.
	 */
	public unsubscribe() {
		this.activePackageSubscription?.unsubscribe();
		this.evaluationErrorSubscription?.unsubscribe();
	}
}
